import { queryOptions, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './query-keys';
import type {
  ListUsersParams,
  UpdateUserTierRequest,
  UpdateUserPermissionsRequest,
} from '@/types/admin';

/**
 * Query options for admin users list
 */
export const adminUsersQueryOptions = (params: ListUsersParams) =>
  queryOptions({
    queryKey: queryKeys.admin.usersList(params),
    queryFn: async () => {
      return api.listUsers(params);
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1,
  });

/**
 * Query options for admin user details
 */
export const adminUserDetailsQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: queryKeys.admin.userDetails(userId),
    queryFn: async () => {
      return api.getUserDetails(userId);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });

/**
 * Hook to fetch paginated list of users (admin only)
 */
export function useAdminUsers(params: ListUsersParams) {
  return useQuery(adminUsersQueryOptions(params));
}

/**
 * Hook to fetch detailed user information (admin only)
 */
export function useAdminUserDetails(userId: string | null) {
  return useQuery({
    ...adminUserDetailsQueryOptions(userId!),
    enabled: !!userId,
  });
}

/**
 * Hook to update user's subscription tier (admin only)
 */
export function useUpdateUserTier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateUserTierRequest) => api.updateUserTier(request),
    onSuccess: (_, variables) => {
      // Invalidate users list
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.users() });
      // Invalidate specific user details
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.userDetails(variables.userId) });
    },
  });
}

/**
 * Hook to update user permissions (admin only)
 */
export function useUpdateUserPermissions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateUserPermissionsRequest) => api.updateUserPermissions(request),
    onSuccess: (_, variables) => {
      // Invalidate users list
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.users() });
      // Invalidate specific user details
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.userDetails(variables.userId) });
      // Invalidate admin status in case the current user's permissions changed
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.status() });
    },
  });
}
